import * as React from "react";

/**
 * Result returned by useSpacer hook.
 */
export interface UseSpacerResult {
  /** Ref to attach to the scroll container (the scrollable viewport) */
  scrollContainerRef: React.RefObject<HTMLDivElement>;

  /** Ref to attach to the spacer element at the bottom of content */
  spacerRef: React.RefObject<HTMLDivElement>;

  /** Ref to attach to the message that should be hoisted to the top */
  messageToHoistRef: React.RefObject<HTMLDivElement>;

  /** Function to manually recompute the spacer height */
  recomputeSpacer: () => void;
}

/**
 * Hook that manages a dynamic spacer element to "hoist" a message to the top of a scroll container.
 *
 * ## How it works:
 *
 * When a user sends a message, we want that message to appear at the TOP of the viewport,
 * even if there's not enough content below it to naturally scroll it there.
 *
 * The spacer creates artificial height at the bottom of the content container:
 *
 * ```
 * ┌─────────────────────────┐
 * │ (scroll-margin-top)     │ ← where the viewport's top lands
 * │ User Message (hoisted)  │
 * ├─────────────────────────┤
 * │ AI Response (growing)   │
 * │ Thinking Indicator      │
 * ├─────────────────────────┤
 * │ SPACER (fills gap)      │
 * │ (container padding)     │
 * └─────────────────────────┘ ← bottom of the scroll
 * ```
 *
 * The spacer height = viewportHeight - contentHeight
 * where contentHeight = everything below the hoisted message's scroll margin
 * except the spacer, padding included. Scrolled to the bottom, the hoisted
 * message then sits exactly where `scrollIntoView({ block: "start" })` puts it.
 *
 * As AI response grows, contentHeight increases, so spacer shrinks proportionally
 * until spacer = 0 when content fills the viewport.
 *
 * ## Requirements:
 *
 * - Call `recomputeSpacer()` whenever content changes (e.g., in a useLayoutEffect
 *   with messages as a dependency).
 *
 * ## What's handled automatically:
 *
 * - Viewport resize (window resize, input field grows/shrinks) via ResizeObserver
 */
export function useSpacer(): UseSpacerResult {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const spacerRef = React.useRef<HTMLDivElement>(null);
  const messageToHoistRef = React.useRef<HTMLDivElement>(null);

  /**
   * Recompute the spacer height based on current DOM positions.
   *
   * spacerHeight = viewportHeight - contentHeight
   *
   * where:
   * - viewportHeight = scroll container's clientHeight
   * - contentHeight = scroll height below the hoisted message's scroll margin,
   *   minus the spacer itself
   */
  const recomputeSpacer = React.useCallback(function recomputeSpacer() {
    if (!scrollContainerRef.current || !spacerRef.current) {
      return;
    }

    const container = scrollContainerRef.current;
    const viewportHeight = container.clientHeight;

    if (messageToHoistRef.current) {
      const hoisted = messageToHoistRef.current;
      const hoistedScrollTop =
        hoisted.getBoundingClientRect().top -
        container.getBoundingClientRect().top -
        container.clientTop +
        container.scrollTop -
        parseFloat(getComputedStyle(hoisted).scrollMarginTop);
      // Resize instantly while a turn runs, so following the bottom stays exact.
      spacerRef.current.style.transition = "none";
      // Measure with a viewport-tall spacer so the content always overflows:
      // while it is shorter than the viewport, scrollHeight reports the
      // viewport's height rather than the content's.
      spacerRef.current.style.height = `${viewportHeight}px`;
      const contentHeight =
        container.scrollHeight - viewportHeight - hoistedScrollTop;

      const spacerHeight = Math.max(0, viewportHeight - contentHeight);
      spacerRef.current.style.height = `${spacerHeight}px`;
    } else {
      // No message to hoist: the turn ended. Collapse over the stylesheet's
      // transition, so lifted content slides back down instead of jumping.
      spacerRef.current.style.transition = "";
      spacerRef.current.style.height = "0px";
    }
  }, []);

  // Set up ResizeObserver to handle viewport changes (window resize, input grows/shrinks)
  React.useLayoutEffect(
    function observeViewport() {
      const observer = new ResizeObserver(recomputeSpacer);

      if (scrollContainerRef.current) {
        observer.observe(scrollContainerRef.current);
      }

      return () => observer.disconnect();
    },
    [recomputeSpacer],
  );

  return {
    scrollContainerRef,
    spacerRef,
    messageToHoistRef,
    recomputeSpacer,
  };
}
